const INTERNAL_URL_ORIGIN = 'https://eliadmin.local';
const DEFAULT_CONFIGURATOR_RETURN_PATH = '/';

export type ConfiguratorSearchParam = string | string[] | undefined;

export function getFirstConfiguratorSearchParam(value: ConfiguratorSearchParam) {
  return Array.isArray(value) ? value[0] : value;
}

function isConfiguratorPath(pathname: string) {
  let decodedPathname = pathname;

  try {
    decodedPathname = decodeURIComponent(pathname);
  } catch {
    // Keep the original pathname when a malformed escape sequence is supplied.
  }

  const segments = decodedPathname.split('/').filter(Boolean);
  const firstSegment = segments[0]?.toLowerCase();
  const secondSegment = segments[1]?.toLowerCase();
  const firstSegmentLooksLikeLocale = /^[a-z]{2}(?:-[a-z]{2})?$/i.test(firstSegment ?? '');

  return (
    firstSegment === 'configurator' ||
    (firstSegmentLooksLikeLocale && secondSegment === 'configurator')
  );
}

export function getConfiguratorReturnPath(value: ConfiguratorSearchParam) {
  const candidate = getFirstConfiguratorSearchParam(value)?.trim();

  if (
    !candidate ||
    candidate.length > 2_048 ||
    !candidate.startsWith('/') ||
    candidate.startsWith('//') ||
    candidate.includes('\\') ||
    /[\u0000-\u001f\u007f]/.test(candidate)
  ) {
    return DEFAULT_CONFIGURATOR_RETURN_PATH;
  }

  try {
    const url = new URL(candidate, INTERNAL_URL_ORIGIN);

    if (url.origin !== INTERNAL_URL_ORIGIN || isConfiguratorPath(url.pathname)) {
      return DEFAULT_CONFIGURATOR_RETURN_PATH;
    }

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return DEFAULT_CONFIGURATOR_RETURN_PATH;
  }
}

type BuildConfiguratorHrefOptions = {
  confId?: string | number;
  cartHash?: string | null;
  returnPath?: ConfiguratorSearchParam;
};

export function buildConfiguratorHref(
  configuratorPath: string,
  { confId, cartHash, returnPath }: BuildConfiguratorHrefOptions,
) {
  const url = new URL(configuratorPath, INTERNAL_URL_ORIGIN);

  if (confId !== undefined) {
    url.searchParams.set('conf_id', String(confId));
  }

  if (cartHash) {
    url.searchParams.set('cart_hash', cartHash);
  }

  if (returnPath !== undefined) {
    url.searchParams.set('return_to', getConfiguratorReturnPath(returnPath));
  }

  return `${url.pathname}${url.search}${url.hash}`;
}
